// src/pages/StaffDashboard.js (v4 – centralized media URLs + video support)

import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import api from "../services/api";
import { API_PATHS } from "../config";
import { getMediaUrls } from "../utils/media";
import timeAgo from "../utils/timeAgo";
import "../styles/StaffDashboard.css";

const PAGE_SIZE = 20;

/**
 * Dashboard for staff users.
 *
 * Shows profile info, allows display name editing, and displays their uploaded media
 * with infinite scroll and inline story editing.
 *
 * @component
 * @returns {JSX.Element}
 */
const StaffDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");

  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMorePhotos, setHasMorePhotos] = useState(true);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const [newStory, setNewStory] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const observer = useRef(null);

  const token = localStorage.getItem("token");

  // Load profile
  useEffect(() => {
    if (!token) return;

    const fetchProfile = async () => {
      try {
        const { data } = await api.get(API_PATHS.PROFILE);
        setProfile(data);
        setNewDisplayName(data.display_name || "");
      } catch (err) {
        console.error("Failed to load profile:", err);
        setProfileError("Failed to load your profile.");
      }
    };

    fetchProfile();
  }, [token]);

  // Load photos when page changes
  useEffect(() => {
    if (!token || isLoadingPhotos || !hasMorePhotos) return;

    const loadPhotos = async () => {
      setIsLoadingPhotos(true);
      try {
        const { data } = await api.get(API_PATHS.MY_PHOTOS, {
          params: {
            my_photos: true,
            offset: currentPage * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
        });

        const newPhotos = Array.isArray(data) ? data : [];
        setPhotos((prev) =>
          currentPage === 0 ? newPhotos : [...prev, ...newPhotos]
        );
        setHasMorePhotos(newPhotos.length === PAGE_SIZE);
      } catch (err) {
        console.error("Failed to load photos:", err);
        setPhotoError("Failed to load your photos.");
      } finally {
        setIsLoadingPhotos(false);
      }
    };

    loadPhotos();
  }, [token, currentPage, hasMorePhotos, isLoadingPhotos]);

  // Infinite scroll observer
  const lastPhotoRef = (node) => {
    if (observer.current) observer.current.disconnect();
    if (!node || !hasMorePhotos || isLoadingPhotos) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 0.5 }
    );
    observer.current.observe(node);
  };

  // Update display name
  const handleUpdateDisplayName = async () => {
    const trimmed = newDisplayName.trim();
    if (!trimmed) {
      setError("Display name cannot be empty.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      await api.post(API_PATHS.UPDATE_DISPLAY_NAME, {
        new_display_name: trimmed,
      });

      setProfile((prev) => ({ ...prev, display_name: trimmed }));
      setEditingDisplayName(false);
      setMessage("Display name updated!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update display name.");
    } finally {
      setLoading(false);
    }
  };

  // Edit photo story
  const startEdit = (photo) => {
    setEditingPhotoId(photo.id);
    setNewStory(photo.story || "");
    setError("");
  };

  const cancelEdit = () => {
    setEditingPhotoId(null);
    setNewStory("");
  };

  const saveStory = async (photoId) => {
    const trimmed = newStory.trim();
    if (!trimmed) {
      setError("Story cannot be empty.");
      return;
    }

    setLoading(true);
    try {
      await api.post(API_PATHS.UPDATE_PHOTO_STORY(photoId), {
        new_story: trimmed,
      });

      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, story: trimmed } : p))
      );
      setEditingPhotoId(null);
      setNewStory("");
      setMessage("Story updated!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update story.");
    } finally {
      setLoading(false);
    }
  };

  const emailPrefix = profile?.email?.split("@")[0] || "";
  const showNudge = profile?.display_name === emailPrefix;

  return (
    <div className="staff-dashboard page">
      <header className="staff-dashboard-header">
        <div className="staff-dashboard-heading">
          <p className="staff-dashboard-eyebrow">Staff dashboard</p>
          <h1 className="staff-dashboard-title">Welcome back!</h1>
          <p className="staff-dashboard-subtitle">
            Review your profile and keep stories on your uploaded photos up to date.
          </p>
        </div>

        {profile && profile.school_name && (
          <div className="staff-dashboard-meta">
            <span className="staff-dashboard-school-chip">{profile.school_name}</span>
          </div>
        )}
      </header>

      {message && (
        <p className="staff-message staff-message--success" aria-live="polite">
          {message}
        </p>
      )}
      {error && (
        <p className="staff-message staff-message--error" aria-live="polite">
          {error}
        </p>
      )}

      {/* Profile Section */}
      <section className="profile-section">
        <h2>Your Profile</h2>
        {profileError && <p className="staff-inline-error">{profileError}</p>}

        {profile ? (
          <div className="profile-card">
            <p className="staff-field">
              <span className="staff-field-label">Display Name</span>{" "}
              {editingDisplayName ? (
                <span className="staff-field-editor">
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    maxLength={255}
                    className="display-name-input"
                    autoFocus
                  />
                  <div className="staff-edit-actions">
                    <button
                      onClick={handleUpdateDisplayName}
                      disabled={loading}
                      className="staff-btn staff-btn-primary"
                    >
                      {loading ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingDisplayName(false);
                        setNewDisplayName(profile.display_name || "");
                        setError("");
                      }}
                      className="staff-btn staff-btn-ghost"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </span>
              ) : (
                <span className="staff-field-value">
                  {profile.display_name || profile.email}{" "}
                  {showNudge && (
                    <button
                      onClick={() => setEditingDisplayName(true)}
                      className="display-name-cta-button"
                    >
                      Click to Change your Display Name
                      <span className="nudge-arrow" title="Personalize your name!">
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </span>
                    </button>
                  )}
                  {!showNudge && (
                    <button
                      onClick={() => setEditingDisplayName(true)}
                      className="staff-btn staff-btn-ghost"
                    >
                      Edit
                    </button>
                  )}
                </span>
              )}
            </p>

            <p className="staff-field">
              <span className="staff-field-label">Email</span>{" "}
              <span className="staff-field-value">{profile.email}</span>
            </p>

            <p className="staff-field">
              <span className="staff-field-label">Role</span>{" "}
              <span className="staff-field-value">{profile.role}</span>
            </p>

            <p className="staff-field">
              <span className="staff-field-label">School</span>{" "}
              <span className="staff-field-value">
                {profile.school_name || "No school assigned"}
              </span>
            </p>
          </div>
        ) : (
          <p className="staff-inline-muted">Loading profile…</p>
        )}
      </section>

      {/* Your Photos Section */}
      <section className="your-photos-section">
        <h2>Your Uploaded Photos</h2>
        {photoError && <p className="staff-inline-error">{photoError}</p>}

        {photos.length > 0 ? (
          <div className="photos-grid">
            {photos.map((photo, index) => {
              const { isVideo, isProcessingVideo, imageUrl, videoUrl, posterUrl } = getMediaUrls(photo);

              const durationSeconds =
                photo.video_duration_ms > 0
                  ? Math.round(photo.video_duration_ms / 1000)
                  : null;

              return (
                <div
                  key={photo.id}
                  className="photo-card"
                  ref={index === photos.length - 1 ? lastPhotoRef : null}
                >
                  {isVideo ? (
                    isProcessingVideo ? (
                      <div className="photo-video-processing">
                        <div className="photo-video-spinner" />
                        <span>Processing video…</span>
                      </div>
                    ) : videoUrl ? (
                      <div className="photo-video-wrapper">
                        <video
                          src={videoUrl}
                          poster={posterUrl || undefined}
                          className="photo-thumbnail"
                          playsInline
                          controls
                          preload="metadata"
                          muted
                        />
                        <div className="photo-video-pill">
                          <span>▶ Video</span>
                          {durationSeconds !== null && (
                            <span className="photo-video-duration">{durationSeconds}s</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="photo-missing-media">Video not available.</div>
                    )
                  ) : imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={photo.story || "Your photo"}
                      className="photo-thumbnail"
                      loading="lazy"
                    />
                  ) : (
                    <div className="photo-missing-media">Image not available.</div>
                  )}

                  {editingPhotoId === photo.id ? (
                    <div className="edit-story-form">
                      <textarea
                        value={newStory}
                        onChange={(e) => setNewStory(e.target.value)}
                        placeholder="Edit story and hashtags"
                        className="form-textarea"
                        maxLength={255}
                        rows={4}
                      />
                      <div className="edit-story-actions">
                        <button
                          onClick={() => saveStory(photo.id)}
                          disabled={loading}
                          className="save-story-button"
                        >
                          {loading ? "Saving…" : "Save"}
                        </button>
                        <button onClick={cancelEdit} className="cancel-story-button">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="photo-story">{photo.story || "No story added"}</p>
                      <p className="photo-meta">
                        Uploaded {photo.created_at ? timeAgo(photo.created_at) : "N/A"}
                        {isVideo && (
                          <>
                            {" • "}Video
                            {durationSeconds !== null && ` · ${durationSeconds}s`}
                          </>
                        )}
                      </p>
                      <button
                        onClick={() => startEdit(photo)}
                        className="edit-story-button"
                      >
                        Edit Story
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="staff-inline-muted">No approved photos uploaded yet.</p>
        )}

        {isLoadingPhotos && <p className="loading-indicator">Loading more photos…</p>}
        {!hasMorePhotos && photos.length > 0 && (
          <p className="no-more-photos">No more photos to load.</p>
        )}
      </section>
    </div>
  );
};

export default StaffDashboard;
